<script>
    import dressing from "@intellectability/platform-common";

    import {ACTIONS} from "@intellectability/platform-common/constants";

    import {
        AutoUpdateInput,
        Button,
        EntityChangeHistory,
        Icon, MegaPopup,
        PasswordInput,
        PasswordRules,
        PhoneNumberForm,
        toast
    } from "@intellectability/platform-common/sv";
    import {userSecurity} from "@intellectability/platform-common/sv/state";

    import {actionCheck, create_security_error} from "@intellectability/platform-common/utils";

    import {MediaQuery} from "svelte/reactivity";
    // 1200px is the Bootstrap xl media breakpoint
    const xl = new MediaQuery("(min-width: 1200px)");

    import {
        Col,
        Row
    } from "@sveltestrap/sveltestrap";

    import {createForm} from "svelte-forms-lib";
    import * as yup from "yup";

    /* @type {string|number} profileID */
    let {id: profileID, profile = $bindable(), isActiveProfile = false} = $props();

    const SCOPES = {
        CASE_LOAD: 1,
        PROVIDER: 2,
        REGION: 3,
        STATE: 4
    };

    let s = new Map();
    s.set("CASE_LOAD", SCOPES.CASE_LOAD);
    s.set("PROVIDER", SCOPES.PROVIDER);
    s.set("REGION", SCOPES.REGION);
    s.set("STATE", SCOPES.STATE);

    const {
        USER_DETAILS_CREATE,
        USER_DETAILS_UPDATE,
        USER_EMPLOYER_UPDATE,
        USER_MASTER_ACCOUNT_SETTINGS_EDIT
    } = ACTIONS;

    let hasAccess = $derived(actionCheck(userSecurity.userActions));
    let isLoggedIn = $derived(userSecurity.loggedIn === true);
    let loggedInUserID = $derived(Number(userSecurity.loggedInUser.id) || "");
    let ownProfile = $derived(loggedInUserID === profileID);
    let addressableScopes = $derived(Object.keys(SCOPES).filter(scope => {
        return SCOPES[scope] <= userSecurity.loggedInUser.scope;
    }));

    // for dropdowns
    let userTitles = $state.raw([]);
    let userStatuses = $state.raw([]);
    let cellCarriers = $state.raw([]);
    let regions = $state.raw([]);
    let supervisors = $state.raw([]);
    let providers = $state.raw([]);
    let roles = $state.raw([]);
    let masters = $state.raw([]);

    // Disabling certain fields, which can then only be enabled by certain user actions/scopes
    let canChangeEmployer = $derived.by(function () {

        if (!isActiveProfile) {
            return false;
        }

        // To update employers, user has to have update permissions and the correct scope
        const scope = parseInt(userSecurity.loggedInUser.scope, 10);
        return hasAccess(USER_EMPLOYER_UPDATE) && [SCOPES.REGION, SCOPES.STATE].includes(scope);
    });

    let masterDisabled = $derived(!hasAccess(USER_MASTER_ACCOUNT_SETTINGS_EDIT) || !isActiveProfile);

    let canEdit = $derived(hasAccess(USER_DETAILS_UPDATE) || hasAccess(USER_DETAILS_CREATE));

    // Allowing any user to be a supervisor (when the business rule is enabled)
    let allowAnyUserAsSupervisor = $derived(userSecurity.businessRules
        .some(rule => rule.rule_name === "allow_any_user_as_supervisor")
    );

    let loading = $state(true);

    let isExistingUser = $derived(Number.isInteger(parseInt(profileID, 10)));

    let formDisabled = $derived.by(function () {

        if (isExistingUser) {

            if (!isActiveProfile) {
                return true; // inactive profiles cannot be edited within the application
            }

            if (ownProfile === true) {  // Users can always update their own account
                return false;
            }

            return hasAccess(USER_DETAILS_UPDATE) === false;
        }

        return hasAccess(USER_DETAILS_CREATE) === false;
    });

    /**
     * @typedef {object} UserDetails
     * @property {string} calendar
     * @property {string} cell_carrier
     * @property {string} credentials
     * @property {string} email
     * @property {string} employer
     * @property {number|null} employer_id
     * @property {string} first_name
     * @property {string} hire_date
     * @property {number|null} id
     * @property {boolean} is_master
     * @property {string} last_name
     * @property {string} licence_number
     * @property {string} master_username
     * @property {string} middle_name
     * @property {number|null} region
     * @property {array} roles
     * @property {number|null} scope
     * @property {string} signature
     * @property {number|null} status
     * @property {number|null} supervisor
     * @property {string} supervisor_name
     * @property {string} title
     * @property {string} username
     */

    /** @type {UserDetails|{}} */
    let userDetails = $derived(profile);

    const applicable_business_rule_names = [
        "require_user_phone",
        "show_employer_other_id",
        "allow_any_user_as_supervisor",
        "allow_multiple_providers_when_requesting_accounts",
        "apply_user_list_security"
    ];

    let applicable_business_rules = $derived.by(function () {
        const rules = userSecurity.businessRules.filter(function (rule) {
            return applicable_business_rule_names.includes(rule.rule_name);
        });
        return rules || [];
    });

    const form_props = {
        validationSchema: yup.object().shape({
            first_name: yup.string().required(),
            last_name: yup.string().required(),
            title: yup.string().required(),
            email: yup.string().email().required(),
            employer: yup.string().required(),
            region: yup.string().notOneOf([""]).required(),
            status: yup.string().required(),

            calendar: yup.string().url("Please enter a valid link")
        }),
        onSubmit: values => {
            // submit
            debugger;
        }
    };

    const {form, errors, handleSubmit, updateInitialValues} = createForm({
        initialValues: userDetails,
        validationSchema: form_props.validationSchema,
        onSubmit: form_props.onSubmit
    });

    let defaultInputProps = $derived({
        autoupdate: isExistingUser,
        entityID: profileID,
        route: isExistingUser ? `/users/update/${profileID}` : null,
        loading
    });

    let showChangeLog = $state(false);

    $effect(function () {
        // allows us to update the initial form values when changed via an
        // api call (update or load)
        updateInitialValues(profile);
    });

    $effect(function load() {

        if (isLoggedIn) {

            const user_employer_id = (userSecurity?.loggedInUser?.employer_id) || 0;

            // actions check
            if (!profileID) {
                if (!hasAccess(USER_DETAILS_CREATE)) {
                    const security_error = create_security_error("UserDetails.svelte", "UserDetailsForm",
                        USER_DETAILS_CREATE);
                    dressing.api.report_security_error(security_error)
                    .then(function () {
                        // regardless of response, log out
                        dressing.api.logout();
                    });
                    return;
                }
            }

            let needed_initial_data = [
                dressing.get("/users/titles").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {titles} = data;
                        if (Array.isArray(titles) && titles.length > 0) {
                            userTitles = titles.map(function (title) {
                                return title.title;
                            });
                        }
                    }
                    return success;
                }),
                dressing.get("/users/statuses").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const statuses = data.users_statuses;
                        if (Array.isArray(statuses) && statuses.length > 0) {
                            // convert to an options-ready array
                            userStatuses = statuses.map(function (status) {
                                return {
                                    value: status.id.toString(),
                                    label: status.status
                                };
                            });
                        }
                    }
                    return success;
                }),
                dressing.get("/application/cell_carriers").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const _cell_carriers = data.cellCarriers;
                        if (Array.isArray(_cell_carriers) && _cell_carriers.length > 0) {
                            cellCarriers = _cell_carriers.map(function (carrier) {
                                return {
                                    value: carrier.id + "",
                                    label: carrier.carrier
                                };
                            });
                        }
                    }
                    return success;
                }),
                dressing.get("/application/regions").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const _regions = data.regions;
                        regions = _regions.map(function (region) {
                            return {
                                label: region.reg_name,
                                value: region.reg_id + ""
                            };
                        });
                    }
                    return success;
                }),
                dressing.get(`/providers/select/user_details/${profileID ?? 0}`).then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const _providers = data;
                        if (Array.isArray(_providers) && _providers.length > 0) {
                            providers = _providers.map(function (provider) {
                                return {
                                    value: provider.prv_id,
                                    label: provider.prv_name
                                };
                            });
                        }
                    }
                    return success;
                }),
                dressing.get("/users/roles").then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const {users_roles} = data;

                        if (Array.isArray(users_roles) && users_roles.length > 0) {
                            roles = users_roles.toSorted(function (a, b) {
                                return a.role_name.localeCompare(b.role_name);
                            });
                        }

                    }
                    return success;
                })
            ];

            if (hasAccess(USER_DETAILS_UPDATE)) {
                needed_initial_data.push(dressing.get(`/providers/supervisors/${user_employer_id}`)
                    .then(function ({data: res}) {
                        const {success, data} = res;
                        if (success === true) {
                            const supervisors_list = data.supervisors;
                            if (Array.isArray(supervisors_list) && supervisors_list.length > 0) {
                                supervisors = supervisors_list.map(function (supervisor) {
                                    return {
                                        value: supervisor.id,
                                        label: supervisor.name
                                    };
                                });
                            }
                        }
                        return success;
                    })
                );
            }

            if (!masterDisabled && profileID) {
                needed_initial_data.push(dressing.get(`/users/masters/${profileID}`).then(({data: res}) => {
                    const {success, data} = res;
                    if (success === true) {
                        const _masters = data.masters;
                        if (Array.isArray(_masters) && _masters.length > 0) {
                            masters = _masters.map(function (master) {
                                return {
                                    value: master.username,
                                    label: master.name
                                };
                            });
                        }
                    }
                    return success;
                }));
            }

            Promise.allSettled(needed_initial_data)
            .then(function (resolved) {
                const result = resolved.every(r => r.value === true);
                if (result) {
                    loading = false;
                }
                else {
                    toast.error("An error occurred while loading the user details", {
                        action: {
                            label: "@ support",
                            onClick: () => {
                                window.location.href = "https://support.replacingrisk.com/portal/en/newticket";
                            }
                        }
                    });
                }
            });
        }
    });

    function on_update({field, value}) {
        userDetails[field] = value;
    }

    function on_update_employer({value}) {
        userDetails.employer_id = value;
        if (!allowAnyUserAsSupervisor) {
            userDetails.supervisor = null; // unset existing supervisor
            get_supervisors(value); // fetch supervisors
        }
    }

    function get_supervisors(employer_id = 0) {

        dressing.get(`/providers/supervisors/${employer_id}`)
        .then(function ({data: res}) {
            const {success, data} = res;
            if (success === true) {
                const supervisors_list = data.supervisors;
                if (Array.isArray(supervisors_list) && supervisors_list.length > 0) {
                    // convert to an options-ready array
                    supervisors = supervisors_list.map(function (supervisor) {
                        return {
                            value: supervisor.id,
                            label: supervisor.name
                        };
                    });
                }
            } else {
                toast.error("An error occurred while loading the supervisors list", {
                    action: {
                        label: "@ support",
                        onClick: () => {
                            window.location.href = "https://support.replacingrisk.com/portal/en/newticket";
                        }
                    }
                });
            }
        });
    }

    import "https://early.webawesome.com/webawesome@3.0.0-alpha.13/dist/components/split-panel/split-panel.js";
</script>

<div class="mb-3">
    <h2>
        <Icon icon="id-card" class="me-1"/>
        Details
    </h2>
</div>

{#if xl.current}
    <form onsubmit={handleSubmit} novalidate>
        <wa-split-panel
                position="70"
                style="--min: 200px; --max: calc(100% - 150px);"
        >
            <div slot="start">
                {@render userForm()}
            </div>
            <div slot="end">
                {@render rolesForm()}
            </div>
        </wa-split-panel>
    </form>
{:else}
    <form onsubmit={handleSubmit} novalidate>
        <div id="user-form">
            {@render userForm()}
        </div>
        <div id="user-roles">
            {@render rolesForm()}
        </div>
    </form>
{/if}

{#snippet userForm()}
    <div class="pe-3">
        {#key userDetails}
            <fieldset disabled={loading} id="loading-fieldset">
                <div class="callout">
                    <h3>Account Info</h3>
                    <div class="d-flex justify-content-between align-items-center">
                        <a href="#user-roles" class="link-primary text-decoration-none d-xl-none">
                            View roles
                            <Icon icon="chevrons-down" family="duotone" class="ms-2"/>
                        </a>
                    </div>
                    <Row class="">
                        <Col md="4" class="mb-3">
                            <AutoUpdateInput
                                    tabIndex="1"
                                    defaultValue={$form.first_name}
                                    disabled={formDisabled}
                                    fieldName="first_name"
                                    invalid={$errors.first_name.length > 0}
                                    invalidMessage="Please enter a first name"
                                    onUpdate={on_update}
                                    label="First Name"
                                    required
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col md="4" class="order-md-last mb-3">
                            <AutoUpdateInput

                                    defaultValue={$form.last_name}
                                    disabled={formDisabled}
                                    fieldName="last_name"
                                    invalid={$errors.last_name.length > 0}
                                    invalidMessage="Please enter a last name"
                                    onUpdate={on_update}
                                    label="Last Name"
                                    required
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col md="4" class="mb-3">
                            <AutoUpdateInput
                                    tabIndex="-1"
                                    defaultValue={$form.middle_name}
                                    disabled={formDisabled}
                                    fieldName="middle_name"
                                    label="Middle Name"
                                    onUpdate={on_update}
                                    {...defaultInputProps}
                            />
                        </Col>
                    </Row>
                    <Row class="mb-3">
                        <Col md="12" class="mb-3">
                            {#if userTitles.length > 0}
                                <AutoUpdateInput
                                        defaultValue={$form.title}
                                        disabled={formDisabled}
                                        fieldName="title"
                                        label="Title"
                                        optionsFrom={userTitles}
                                        type="select"
                                        onUpdate={on_update}
                                        {...defaultInputProps}
                                />
                            {:else}
                                <AutoUpdateInput
                                        defaultValue={$form.title}
                                        disabled={formDisabled}
                                        fieldName="title"
                                        label="Title"
                                        onUpdate={on_update}
                                        {...defaultInputProps}
                                />
                            {/if}
                        </Col>
                        <Col class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.credentials}
                                    disabled={formDisabled}
                                    fieldName="credentials"
                                    label="Credentials"
                                    onUpdate={on_update}
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.licence_number}
                                    disabled={formDisabled}
                                    fieldName="licence_number"
                                    label="RN Licence Number"
                                    onUpdate={on_update}
                                    {...defaultInputProps}
                            />
                        </Col>
                    </Row>
                </div>
                <div class="callout">
                    <h3>Contact Info</h3>
                    <Row>
                        <Col md="6" class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.email}
                                    disabled={formDisabled}
                                    fieldName="email"
                                    invalid={$errors.email.length > 0}
                                    invalidMessage="Please enter a valid email address"
                                    label="Email"
                                    onUpdate={on_update}
                                    required
                                    type="email"
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.cell_carrier}
                                    disabled={formDisabled}
                                    fieldName="cell_carrier"
                                    label="Cell Carrier"
                                    onUpdate={on_update}
                                    optionsFrom={cellCarriers}
                                    type="select"
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col md="12" class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.calendar}
                                    disabled={formDisabled}
                                    fieldName="calendar"
                                    invalid={$errors.calendar.length > 0}
                                    invalidMessage="Please enter a valid calendar Google Calendar link"
                                    label="Google Calendar Link"
                                    placeholder="Enter the link to your Google Calendar"
                                    onUpdate={on_update}
                                    type="url"
                                    {...defaultInputProps}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col class="mb-3">
                            <PhoneNumberForm
                                    disabled={formDisabled}
                                    entityType="user"
                                    label={phoneLabel}
                                    {...defaultInputProps}
                            />
                            {#snippet phoneLabel()}
                                <h4 class="">Phone Numbers</h4>
                            {/snippet}
                        </Col>
                    </Row>
                </div>
                <div class="callout">
                    <h3>System Info</h3>
                    <Row>
                        <Col md="12" class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.username}
                                    disabled={isExistingUser}
                                    fieldName="username"
                                    label="Username"
                                    {loading}
                            />
                        </Col>
                        <Col md="12" class="mb-3">

                            {#if isExistingUser && !canChangeEmployer}

                                <AutoUpdateInput
                                        defaultValue={profile.employer}
                                        disabled
                                        label="Employer"
                                        {loading}
                                />

                            {:else}

                                <AutoUpdateInput
                                        defaultValue={$form.employer_id}
                                        disabled={formDisabled}
                                        fieldName="employer"
                                        invalid={$errors.employer.length > 0}
                                        invalidMessage="Please choose an employer"
                                        label="Employer"
                                        onUpdate={on_update_employer}
                                        optionsFrom={providers}
                                        required
                                        type="select"
                                        {...defaultInputProps}
                                />
                            {/if}
                        </Col>
                        <Col md="6" class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.status}
                                    disabled={formDisabled}
                                    fieldName="status"
                                    invalid={$errors.status.length > 0}
                                    invalidMessage="Please select a status"
                                    label="Status"
                                    optionsFrom={userStatuses}
                                    onUpdate={on_update}
                                    required
                                    type="select"
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col xs="12" md="6" class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.region}
                                    disabled={formDisabled}
                                    fieldName="region"
                                    invalid={$errors.region.length > 0}
                                    invalidMessage="Please select a region"
                                    label="Region"
                                    onUpdate={on_update}
                                    optionsFrom={regions}
                                    required
                                    type="select"
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col xs="12" md="6" class="mb-3">
                            {#if canEdit}
                                <AutoUpdateInput
                                        defaultValue={$form.supervisor}
                                        disabled={formDisabled}
                                        fieldName="supervisor"
                                        label="Supervisor"
                                        onUpdate={on_update}
                                        optionsFrom={supervisors}
                                        type="select"
                                        {...defaultInputProps}
                                />
                            {:else}
                                <AutoUpdateInput
                                        defaultValue={profile.supervisor_name}
                                        disabled={true}
                                        label="Supervisor"
                                        {...defaultInputProps}
                                />
                            {/if}
                        </Col>
                        <Col class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.hire_date}
                                    disabled={formDisabled}
                                    fieldName="hire_date"
                                    label="Hire Date"
                                    onUpdate={on_update}
                                    type="date"
                                    {...defaultInputProps}
                            />
                        </Col>
                        <Col class="mb-3">
                            <AutoUpdateInput
                                    defaultValue={$form.scope}
                                    disabled={formDisabled}
                                    fieldName="scope"
                                    label="Scope"
                                    onUpdate={on_update}
                                    type="select"
                                    searchable={false}
                                    optionsFrom={addressableScopes.map((key) => {
                                        const text = key.toLowerCase().replace(/_/g, " ");
                                        const value = SCOPES[key];
                                        return {
                                            label: (text.charAt(0).toUpperCase() + text.slice(1)),
                                            value: value
                                        };
                                    })}
                                    {...defaultInputProps}
                            />
                        </Col>
                        {#if isExistingUser && !masterDisabled}
                            <Col xs="12" md="6" class="mb-3">
                                <AutoUpdateInput
                                        defaultValue={$form.master_username}
                                        disabled={masterDisabled}
                                        fieldName="master_username"
                                        label="Master Account"
                                        onUpdate={on_update}
                                        optionsFrom={masters}
                                        type="select"
                                        {...defaultInputProps}
                                />
                            </Col>
                            <Col xs="12" md="6" class="mb-3">
                                <AutoUpdateInput
                                        defaultValue={$form.is_master}
                                        disabled={masterDisabled}
                                        fieldName="is_master"
                                        label="Is a Master Account"
                                        onUpdate={on_update}
                                        type="switch"
                                        {...defaultInputProps}
                                />
                            </Col>
                        {/if}
                    </Row>
                </div>

                {#if ownProfile}
                    <div class="callout">
                        <h3>Signature</h3>
                        <AutoUpdateInput
                                defaultValue={$form.signature}
                                disabled={formDisabled || !ownProfile}
                                fildName="signature"
                                label=""
                                onUpdate={on_update}
                                type="signature"
                                {...defaultInputProps}
                        />
                    </div>

                    <div class="callout">
                        <h3>Update Password</h3>
                        <Row>
                            <Col class="mb-3">
                                <PasswordInput
                                        userID={profileID}
                                        confirmPassword
                                        onupdate={() => toast.success("Password updated successfully")}
                                />
                            </Col>
                            <Col xs="12">
                                <PasswordRules/>
                            </Col>
                        </Row>
                    </div>
                {/if}
            </fieldset>
        {/key}

        <div>
            {#if isExistingUser}
                <Button id="change_log_button" variant="brand" size="small"
                        onclick={() => showChangeLog = !showChangeLog}>
                    <Icon icon="clock-rotate-left" class="me-2"/>
                    Change Log
                </Button>
                <MegaPopup
                        bind:active={showChangeLog}
                        for="change_log_button"
                        placement="top-start">
                    <div class="py-2 px-3 rounded-1 bg-white">
                        {@render changeHistory()}
                    </div>
                </MegaPopup>
            {/if}
        </div>

        {#if !isExistingUser && hasAccess(USER_DETAILS_CREATE)}
            <Button variant="brand" disabled={$errors.length > 0} type="submit">
                Add new user
            </Button>
        {/if}

    </div>
{/snippet}

{#snippet changeHistory()}
    <EntityChangeHistory entityType={"user"} entityID={profileID}></EntityChangeHistory>
{/snippet}

{#snippet rolesForm()}
    {@const disabled = loading || ownProfile || !isActiveProfile}
    <wa-callout appearance={disabled ? "plain" : "filled"} class="h-100 ms-3">
        <div class="d-flex justify-content-between align-items-center">
            <h3>Roles</h3>
            <div>
                <a href="#user-form" class="link-primary text-decoration-none d-xl-none">
                    View form
                    <Icon icon="chevrons-up" family="duotone" class="ms-2"/>
                </a>
                <!-- Cannot modify own roles -->
                {#if ownProfile}
                    <wa-icon id={"roles-disabled-tooltip"}
                             name="circle-question"
                             variant="solid"
                             style="font-size: 1rem; color: var(--hrst-error-500)"></wa-icon>
                    <wa-tooltip
                            for={"roles-disabled-tooltip"}
                            placement="top-end"
                            distance="20"
                            style="--padding: 1.25rem; --max-width: 20rem;"
                    >
                        <div>
                            Users may not update their own roles.
                        </div>
                    </wa-tooltip>
                {/if}
            </div>
        </div>
        <!-- Users may not update their own roles -->
        <fieldset {disabled}>
            {#each roles as role}
                {@render roleToggle(role)}
                <wa-divider style="--spacing: .375rem;"></wa-divider>
            {/each}
        </fieldset>
    </wa-callout>
{/snippet}

{#snippet roleToggle(role)}
    {@const selected = userDetails.roles.includes(role.role_id)}
    <!-- keeps ids unique when generated between xl and below -->
    {@const rand = ((Math.random() + Math.random())).toString(36).substring(2)}
    <div class="d-flex align-items-center justify-content-between">
        <AutoUpdateInput
                type="switch"
                id={"role-" + role.role_id}
                autoupdate={false}
                defaultValue={selected}
                label={role.role_name}
                size="small"
                onupdate={(value) => {}}
        />

        {#if role.role_description}
            <wa-icon-button id={"role-tooltip-" + role.role_id + rand} name="circle-info"
                            style="font-size: .75rem;"></wa-icon-button>
            <wa-tooltip
                    for={"role-tooltip-" + role.role_id + rand} placement="top-end"
                    style="--padding: 1.25rem; --max-width: 20rem;"
            >
                <div>
                    <div class="fs-6 text-primary-200">
                        {role.role_name}
                    </div>
                    <div>
                        {role.role_description}
                    </div>
                    {#if role.training}
                        <div class="fs-6 text-primary-200">
                            Related Training
                        </div>
                        <div>
                            {role.training}
                        </div>

                    {/if}
                </div>
            </wa-tooltip>
        {/if}
    </div>
{/snippet}

<style>
    .callout {
        border: 1px solid #ccc;
        padding: 1rem 1.75rem;
        border-radius: 0.5rem;
        margin-bottom: 1.5rem;
    }

    .callout > h3 {
        margin-bottom: 1.5rem;
    }
</style>